import React, { useEffect, useState } from 'react';
import { ChevronLeft, Search, Users, User, Send, Paperclip, Smile, MoreVertical, Phone, Video, Info, Image as ImageIcon, File, Check, CheckCheck } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
interface ChatPageProps {
  onBack: () => void;
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
}
type ChatType = 'direct' | 'group';
interface Chat {
  id: number;
  type: ChatType;
  name: string;
  avatar?: string;
  initials: string;
  lastMessage: string;
  timestamp: string;
  unreadCount: number;
  isOnline?: boolean;
  members?: string[];
}
interface Message {
  id: number;
  senderId: number;
  senderName: string;
  senderInitials: string;
  content: string;
  timestamp: string;
  isRead: boolean;
  isSent: boolean;
}
export function ChatPage({
  onBack,
  onNetworkClick,
  onInventoryClick,
  onRMIClick
}: ChatPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messageText, setMessageText] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'direct' | 'groups'>('all');
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Current logged-in user
  const currentUser = {
    id: 1,
    name: 'Sarah Ahmed',
    initials: 'SA'
  };
  // Mock chats data
  const [chats] = useState<Chat[]>([{
    id: 1,
    type: 'direct',
    name: 'Mohamed Ali',
    initials: 'MA',
    lastMessage: 'Can you share the property details?',
    timestamp: '2m ago',
    unreadCount: 2,
    isOnline: true
  }, {
    id: 2,
    type: 'group',
    name: 'Sales Team',
    initials: 'ST',
    lastMessage: 'Ahmed: Meeting at 3 PM today',
    timestamp: '15m ago',
    unreadCount: 5,
    members: ['Ahmed Hassan', 'Karim Mahmoud', 'Nadia Hassan', 'You']
  }, {
    id: 3,
    type: 'direct',
    name: 'Ahmed Hassan',
    initials: 'AH',
    lastMessage: 'Thanks for the update!',
    timestamp: '1h ago',
    unreadCount: 0,
    isOnline: false
  }, {
    id: 4,
    type: 'group',
    name: 'New Cairo Projects',
    initials: 'NC',
    lastMessage: 'Sarah: New listing added',
    timestamp: '2h ago',
    unreadCount: 3,
    members: ['Mohamed Ali', 'Sarah Ahmed', 'Omar Khaled', 'Laila Ibrahim']
  }, {
    id: 5,
    type: 'direct',
    name: 'Karim Mahmoud',
    initials: 'KM',
    lastMessage: 'See you tomorrow',
    timestamp: 'Yesterday',
    unreadCount: 0,
    isOnline: true
  }, {
    id: 6,
    type: 'group',
    name: 'Marketing Team',
    initials: 'MT',
    lastMessage: 'Nadia: Campaign results are ready',
    timestamp: 'Yesterday',
    unreadCount: 0,
    members: ['Nadia Hassan', 'Omar Khaled', 'Laila Ibrahim']
  }]);
  // Mock messages for selected chat
  const [messages] = useState<Message[]>([{
    id: 1,
    senderId: 2,
    senderName: 'Mohamed Ali',
    senderInitials: 'MA',
    content: 'Hi Sarah, do you have the details for the villa in 5th Settlement?',
    timestamp: '10:30 AM',
    isRead: true,
    isSent: true
  }, {
    id: 2,
    senderId: 1,
    senderName: 'Sarah Ahmed',
    senderInitials: 'SA',
    content: 'Yes, let me send you the brochure',
    timestamp: '10:32 AM',
    isRead: true,
    isSent: true
  }, {
    id: 3,
    senderId: 1,
    senderName: 'Sarah Ahmed',
    senderInitials: 'SA',
    content: 'The property is 320 sqm with 3 bedrooms and a garden',
    timestamp: '10:33 AM',
    isRead: true,
    isSent: true
  }, {
    id: 4,
    senderId: 2,
    senderName: 'Mohamed Ali',
    senderInitials: 'MA',
    content: "Perfect! What's the price range?",
    timestamp: '10:35 AM',
    isRead: true,
    isSent: true
  }, {
    id: 5,
    senderId: 1,
    senderName: 'Sarah Ahmed',
    senderInitials: 'SA',
    content: '2.4M EGP',
    timestamp: '10:36 AM',
    isRead: false,
    isSent: true
  }, {
    id: 6,
    senderId: 2,
    senderName: 'Mohamed Ali',
    senderInitials: 'MA',
    content: 'Can you share the property details?',
    timestamp: '10:38 AM',
    isRead: false,
    isSent: true
  }]);
  const handleSendMessage = () => {
    if (messageText.trim()) {
      // In a real app, this would send to backend
      console.log('Sending message:', messageText);
      setMessageText('');
    }
  };
  const filteredChats = chats.filter(chat => {
    const matchesSearch = chat.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || activeTab === 'direct' && chat.type === 'direct' || activeTab === 'groups' && chat.type === 'group';
    return matchesSearch && matchesTab;
  });
  const renderChatList = () => <>
      {/* Search Bar */}
      <div className="px-4 pt-4 pb-3 bg-white sticky top-[72px] z-10 border-b border-gray-100" data-id="element-236">
        <div className="relative" data-id="element-237">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-238" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search conversations..." className="w-full pl-11 pr-4 py-2.5 bg-neutral-bg border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20" data-id="element-239" />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 py-3 bg-white border-b border-gray-100" data-id="element-240">
        <div className="flex gap-2" data-id="element-241">
          <button onClick={() => setActiveTab('all')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${activeTab === 'all' ? 'bg-secondary text-white' : 'bg-neutral-bg text-gray-600'}`} data-id="element-242">
            All
          </button>
          <button onClick={() => setActiveTab('direct')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${activeTab === 'direct' ? 'bg-secondary text-white' : 'bg-neutral-bg text-gray-600'}`} data-id="element-243">
            Direct
          </button>
          <button onClick={() => setActiveTab('groups')} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${activeTab === 'groups' ? 'bg-secondary text-white' : 'bg-neutral-bg text-gray-600'}`} data-id="element-244">
            Groups
          </button>
        </div>
      </div>

      {/* Chat List */}
      <div className="divide-y divide-gray-100" data-id="element-245">
        {filteredChats.map(chat => <button key={chat.id} onClick={() => setSelectedChat(chat)} className="w-full px-4 py-4 flex items-start gap-3 hover:bg-neutral-bg transition-colors text-left" data-id="element-246">
            {/* Avatar */}
            <div className="relative flex-shrink-0" data-id="element-247">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${chat.type === 'group' ? 'bg-purple-100 text-purple-600' : 'bg-secondary/20 text-secondary'}`} data-id="element-248">
                {chat.type === 'group' ? <Users className="w-6 h-6" data-id="element-249" /> : <span className="text-sm font-bold" data-id="element-250">{chat.initials}</span>}
              </div>
              {chat.type === 'direct' && chat.isOnline && <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" data-id="element-251"></div>}
            </div>

            {/* Chat Info */}
            <div className="flex-1 min-w-0" data-id="element-252">
              <div className="flex items-start justify-between mb-1" data-id="element-253">
                <h3 className="font-semibold text-neutral-text truncate" data-id="element-254">
                  {chat.name}
                </h3>
                <span className="text-xs text-gray-400 ml-2 flex-shrink-0" data-id="element-255">
                  {chat.timestamp}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-256">
                <p className="text-sm text-gray-600 truncate flex-1" data-id="element-257">
                  {chat.lastMessage}
                </p>
                {chat.unreadCount > 0 && <span className="ml-2 bg-secondary text-white text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0" data-id="element-258">
                    {chat.unreadCount}
                  </span>}
              </div>
              {chat.type === 'group' && chat.members && <p className="text-xs text-gray-400 mt-1" data-id="element-259">
                  {chat.members.length} members
                </p>}
            </div>
          </button>)}
      </div>
    </>;
  const renderChatView = () => {
    if (!selectedChat) return null;
    return <div className="flex flex-col h-screen" data-id="element-260">
        {/* Chat Header */}
        <div className="bg-primary px-4 py-4 safe-top flex items-center justify-between sticky top-0 z-10 shadow-sm" data-id="element-261">
          <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-262">
            <button onClick={() => setSelectedChat(null)} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-full transition-colors flex-shrink-0" data-id="element-263">
              <ChevronLeft className="w-6 h-6" data-id="element-264" />
            </button>

            <div className="relative flex-shrink-0" data-id="element-265">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedChat.type === 'group' ? 'bg-purple-100 text-purple-600' : 'bg-white/20 text-white'}`} data-id="element-266">
                {selectedChat.type === 'group' ? <Users className="w-5 h-5" data-id="element-267" /> : <span className="text-sm font-bold" data-id="element-268">
                    {selectedChat.initials}
                  </span>}
              </div>
              {selectedChat.type === 'direct' && selectedChat.isOnline && <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-primary" data-id="element-269"></div>}
            </div>

            <div className="flex-1 min-w-0" data-id="element-270">
              <h2 className="font-semibold text-white truncate" data-id="element-271">
                {selectedChat.name}
              </h2>
              {selectedChat.type === 'direct' ? <p className="text-xs text-white/70" data-id="element-272">
                  {selectedChat.isOnline ? 'Online' : 'Offline'}
                </p> : <p className="text-xs text-white/70" data-id="element-273">
                  {selectedChat.members?.length} members
                </p>}
            </div>
          </div>

          <div className="flex gap-2" data-id="element-274">
            <button className="p-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-275">
              <Phone className="w-5 h-5" data-id="element-276" />
            </button>
            <button className="p-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-277">
              <Video className="w-5 h-5" data-id="element-278" />
            </button>
            <button className="p-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-279">
              <Info className="w-5 h-5" data-id="element-280" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto bg-neutral-bg px-4 py-4 space-y-4" data-id="element-281">
          {messages.map(message => {
          const isCurrentUser = message.senderId === currentUser.id;
          return <div key={message.id} className={`flex gap-2 ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-282">
                {!isCurrentUser && <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0" data-id="element-283">
                    <span className="text-xs font-bold text-secondary" data-id="element-284">
                      {message.senderInitials}
                    </span>
                  </div>}

                <div className={`flex flex-col ${isCurrentUser ? 'items-end' : 'items-start'} max-w-[75%]`} data-id="element-285">
                  {!isCurrentUser && selectedChat.type === 'group' && <span className="text-xs text-gray-500 mb-1 px-2" data-id="element-286">
                      {message.senderName}
                    </span>}
                  <div className={`rounded-2xl px-4 py-2 ${isCurrentUser ? 'bg-secondary text-white rounded-br-sm' : 'bg-white text-neutral-text rounded-bl-sm'}`} data-id="element-287">
                    <p className="text-sm" data-id="element-288">{message.content}</p>
                  </div>
                  <div className="flex items-center gap-1 mt-1 px-2" data-id="element-289">
                    <span className="text-xs text-gray-400" data-id="element-290">
                      {message.timestamp}
                    </span>
                    {isCurrentUser && (message.isRead ? <CheckCheck className="w-3 h-3 text-blue-500" data-id="element-291" /> : <Check className="w-3 h-3 text-gray-400" data-id="element-292" />)}
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Message Input */}
        <div className="bg-white border-t border-gray-100 px-4 py-3 safe-bottom" data-id="element-293">
          <div className="flex items-end gap-2" data-id="element-294">
            <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-295">
              <Paperclip className="w-5 h-5" data-id="element-296" />
            </button>

            <div className="flex-1 bg-neutral-bg rounded-2xl px-4 py-2 flex items-center gap-2" data-id="element-297">
              <input type="text" value={messageText} onChange={e => setMessageText(e.target.value)} onKeyPress={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }} placeholder="Type a message..." className="flex-1 bg-transparent border-0 focus:outline-none text-sm" data-id="element-298" />
              <button className="p-1 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-299">
                <Smile className="w-5 h-5" data-id="element-300" />
              </button>
            </div>

            <button onClick={handleSendMessage} disabled={!messageText.trim()} className="p-3 bg-secondary text-white rounded-full hover:bg-secondary-dark transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-301">
              <Send className="w-5 h-5" data-id="element-302" />
            </button>
          </div>
        </div>
      </div>;
  };
  return <div className="min-h-screen bg-neutral-bg font-sans w-full" data-id="element-303">
      <div className="w-full mx-auto bg-white min-h-screen relative" data-id="element-304">
        {!selectedChat ? <>
            {/* Header */}
            <div className="bg-primary px-4 py-4 safe-top flex items-center justify-between sticky top-0 z-10 shadow-sm" data-id="element-305">
              <button onClick={onBack} className="p-2 -ml-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-306">
                <ChevronLeft className="w-6 h-6" data-id="element-307" />
              </button>
              <h1 className="font-semibold text-lg text-white" data-id="element-308">Team Chat</h1>
              <button className="p-2 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-309">
                <MoreVertical className="w-6 h-6" data-id="element-310" />
              </button>
            </div>

            {renderChatList()}

            <div className="pb-20" data-id="element-311">
              <BottomNav onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} data-id="element-312" />
            </div>
          </> : renderChatView()}
      </div>
    </div>;
}